import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {createSelector, Store} from '@ngrx/store';
import {MxSelector} from '@aviary/store/store.types';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {BalanceActions} from '@store/balance/balance.actions';
import {BalanceSelectors} from '@store/balance/balance.selectors';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {SeeMoreLessButtonComponent} from '@lib/components/see-more-less-button/see-more-less-button.component';
import {DetailFieldComponent} from '@aviary/components/detail-field/detail-field.component';
import {DetailFieldDefaultsDirective} from '@aviary/components/detail-field/detail-field-defaults/detail-field-defaults.directive';

@Component({
  selector: 'px-account-balance',
  templateUrl: './account-balance.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormatPipe, SelectPipe, FilledButtonComponent, SeeMoreLessButtonComponent, DetailFieldComponent, DetailFieldDefaultsDirective]
})
export class AccountBalanceComponent {
  @Input() set forDependent(value: boolean) {
    this._forDependent = value;
    this.accountSelectors = value ? PatronSelectors.DependentAccountStatus : PatronSelectors.AccountStatus;

    // These need to be recreated here since they depend on this.accountSelectors.
    this.hasMinimumOnlinePaymentBalance = this.createHasMinimumOnlinePaymentBalanceSelector();
    this.makeAPaymentTooltip = this.createMakeAPaymentTooltipSelector();
  }

  public get forDependent(): boolean {
    return this._forDependent;
  }

  public readonly squareAppSelectors = BalanceSelectors.SquareApp;
  public accountSelectors = PatronSelectors.AccountStatus; // This can change to dependent-specific selectors when forDependent is set.
  public hasMinimumOnlinePaymentBalance: MxSelector<boolean> = this.createHasMinimumOnlinePaymentBalanceSelector();
  public makeAPaymentTooltip: MxSelector<string> = this.createMakeAPaymentTooltipSelector();
  public balanceExpanded = false;

  private readonly store = inject(Store);
  private readonly formatPipe: FormatPipe = inject(FormatPipe);
  private _forDependent: boolean = false;

  public toggleExpandedBalance(): void {
    this.balanceExpanded = !this.balanceExpanded;
  }

  public onClickMakeAPayment(): void {
    this.store.dispatch(this._forDependent ? BalanceActions.showMakeAPaymentForDependent() : BalanceActions.showMakeAPayment());
  }

  private createHasMinimumOnlinePaymentBalanceSelector(): MxSelector<boolean> {
    return createSelector(
      this.accountSelectors.currentAccountBalance,
      this.squareAppSelectors.minimumAmount,
      (currentBalance, minimumAmount) => currentBalance > 0 && currentBalance >= minimumAmount
    );
  }

  private createMakeAPaymentTooltipSelector(): MxSelector<string> {
    return createSelector(
      this.accountSelectors.hasBalance,
      this.hasMinimumOnlinePaymentBalance,
      this.squareAppSelectors.minimumAmount,
      (hasBalance, hasMinimumBalance, minimumAmount) => {
        if (hasMinimumBalance) return '';
        if (hasBalance) return `Online payment requires a balance minimum of ${this.formatPipe.transform(minimumAmount, 'CURRENCY')}.`;

        return `There are no charges on your ${this._forDependent ? 'dependent\'s ' : ''}account to pay.`;
      }
    );
  }
}
